/**
 * 媒体服务相关的纯函数、常量与事件类型（无任何运行时依赖，前端 / admin / 主站 / media 服务均从这里引入，
 * 是缩略图命名与规格的唯一来源）：
 *   <dir>/<name>.<ext> → <dir>/<name>.<ext>_thumb_sm.webp（sm）/ _thumb.webp（lg）/ _thumb_xl.webp（xl）
 * 保留原扩展名，避免同目录 photo.png / photo.jpg 共用同一张缩略图。
 */

/** admin「媒体服务」页面的开关；上传端与 media 服务读取同一个 key。 */
export const MEDIA_THUMBNAIL_ENABLED_KEY = 'media_thumbnail_enabled';

/** media 服务只处理该 bucket；其它 bucket 不会有缩略图。 */
export const THUMBNAIL_BUCKET = 'files';

export type ThumbnailSize = 'sm' | 'lg' | 'xl';

export const THUMBNAIL_SUFFIX: Record<ThumbnailSize, string> = { sm: '_thumb_sm', lg: '_thumb', xl: '_thumb_xl' };

/** 生成顺序：sm 最后写入，它存在即表示各档都已生成（用于幂等判断）。 */
export const THUMBNAIL_SIZES: readonly ThumbnailSize[] = ['xl', 'lg', 'sm'];

/**
 * 缩略图规格（与原 Supabase render 转换效果对齐，宽 / 长图不被放大发糊）：
 * - 短边缩放到 shortEdge（cover 方格与按高 / 宽 contain 都够用）；
 * - 长边上限只用于防止极端长宽比（> 8:1）产生超大文件；
 * - 原图更小时不放大。
 * sm 覆盖 ≤256 的展示（含 128 @2x），lg 覆盖 ≤512（画布主图高 512、512 方格），
 * xl 覆盖 ≤1024（视频首帧宽 1024、高 DPR 下的对比预览等）。
 */
export const THUMBNAIL_SPECS: Record<ThumbnailSize, { shortEdge: number; maxLongEdge: number; quality: number }> = {
  sm: { shortEdge: 256, maxLongEdge: 2048, quality: 72 },
  lg: { shortEdge: 512, maxLongEdge: 4096, quality: 78 },
  xl: { shortEdge: 1024, maxLongEdge: 4096, quality: 80 },
};

/** 按规格计算缩略图尺寸（输入为已按 EXIF 方向校正后的宽高）。 */
export function thumbnailDimensions(width: number, height: number, size: ThumbnailSize): { width: number; height: number } {
  const spec = THUMBNAIL_SPECS[size];
  let scale = Math.min(1, spec.shortEdge / Math.min(width, height));
  if (Math.max(width, height) * scale > spec.maxLongEdge) scale = spec.maxLongEdge / Math.max(width, height);
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/** 按展示需要的最长边选择缩略图档位；超过 lg 能提供的尺寸时返回 null（直接用原图）。 */
export function thumbnailSizeFor(requestedLongEdge: number): ThumbnailSize | null {
  if (!(requestedLongEdge > 0)) return 'lg';
  for (const size of ['sm', 'lg', 'xl'] as const) {
    if (requestedLongEdge <= THUMBNAIL_SPECS[size].shortEdge) return size;
  }
  return null;
}

/** 「自动生成缩略图」开关的读取缓存时长（上传端与 media 服务一致）。 */
export const THUMBNAIL_SETTING_CACHE_TTL_MS = 30_000;

/** 解析 admin_setting 中的开关值：默认开启，只有明确为 'false' 时关闭（各端唯一的解析规则）。 */
export function parseThumbnailEnabled(value: string | null | undefined): boolean {
  return value?.trim() !== 'false';
}

/**
 * 创建带缓存的开关读取函数（上传端与 media 服务共用，只有读库方式不同）。
 * 读取失败时视为开启；结果缓存 THUMBNAIL_SETTING_CACHE_TTL_MS。
 * fresh：跳过缓存直接读库（admin 手动触发的操作刚改过开关时使用）。
 */
export function createThumbnailEnabledReader(
  readValue: () => Promise<string | null | undefined>,
): (options?: { fresh?: boolean }) => Promise<boolean> {
  let cache: { value: boolean; ts: number } | undefined;
  return async (options) => {
    if (!options?.fresh && cache && Date.now() - cache.ts < THUMBNAIL_SETTING_CACHE_TTL_MS) return cache.value;
    let value = true;
    try {
      value = parseThumbnailEnabled(await readValue());
    } catch {}
    cache = { value, ts: Date.now() };
    return value;
  };
}

// GIF 不在其中：静态缩略图会丢掉动画，直接展示原图
const THUMBNAIL_SOURCE_EXTS = new Set(['png', 'jpg', 'jpeg', 'webp', 'avif', 'tif', 'tiff']);

/**
 * 视频扩展名：用于从首帧路径反查视频记录、识别视频路径。
 * 比 storage.ts 的识别范围宽：浏览器直传按 MIME（video/*）登记，扩展名可能是 mpeg / mkv 等。
 */
export const VIDEO_EXTS = [
  'mp4', 'webm', 'mov', 'm4v', 'ogv', 'avi', 'mpeg', 'mpg', 'mkv', '3gp', 'flv', 'wmv', 'mts', 'm2ts',
] as const;

export function isThumbnailPath(storagePath: string): boolean {
  return /_thumb(_sm|_xl)?\.webp$/i.test(storagePath);
}

function extOf(storagePath: string): string | null {
  const name = storagePath.slice(storagePath.lastIndexOf('/') + 1);
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1) : null;
}

export function isVideoPath(storagePath: string): boolean {
  const ext = extOf(storagePath);
  return ext !== null && (VIDEO_EXTS as readonly string[]).includes(ext.toLowerCase());
}

/**
 * 解析 Supabase Storage 公网 / render 地址中的 bucket 与对象路径（已解码）；不是此类地址时返回 null。
 * 按路径识别而不限定域名：存储也可能经自定义域名（如 db.flowmuse.ai）访问，
 * 与服务端按 public_url 路径筛选的规则保持一致。
 */
export function parseSupabaseStorageUrl(url: string): { origin: string; bucket: string; path: string } | null {
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    const m = u.pathname.match(/^\/storage\/v1\/(?:object|render\/image)\/public\/([^/]+)\/(.+)$/);
    return m ? { origin: u.origin, bucket: m[1], path: decodeURIComponent(m[2]) } : null;
  } catch {
    return null;
  }
}

// 不展示、无需缩略图的派生文件：局部重绘遮罩（只作为生成输入）。
// 注意视频首/尾帧都可能被「提取首帧 / 尾帧」做成画布上的图片节点，二者都需要缩略图。
const NON_DISPLAY_RE = /(?:^|\/)mask_[^/]*$/i;

export function isThumbnailSource(storagePath: string): boolean {
  if (!storagePath || isThumbnailPath(storagePath) || NON_DISPLAY_RE.test(storagePath)) return false;
  const ext = extOf(storagePath);
  return ext !== null && THUMBNAIL_SOURCE_EXTS.has(ext.toLowerCase());
}

export function thumbnailPath(storagePath: string, size: ThumbnailSize): string {
  return `${storagePath}${THUMBNAIL_SUFFIX[size]}.webp`;
}

/** 某个源文件对应的全部缩略图路径。 */
export function thumbnailPaths(storagePath: string): string[] {
  return THUMBNAIL_SIZES.map((size) => thumbnailPath(storagePath, size));
}

/**
 * 删除资产时应一并删除的派生文件：图片 → 其缩略图。
 * 视频的首 / 尾帧（及其缩略图）保留：「提取首帧 / 尾帧」生成的图片节点会直接引用它们，删掉会让这些节点失效。
 */
export function derivedFilesForDeletion(type: string, storagePath: string): string[] {
  return type === 'image' ? thumbnailPaths(storagePath) : [];
}

/**
 * 视频首帧 / 尾帧路径（rendi 写在视频同目录：<videoBase>_first.jpg / _last.jpg）。
 * 也可用于不带查询参数的公网地址；无扩展名时无法推导，返回 null。
 */
export function videoPosterPaths(videoPath: string): { first: string; last: string } | null {
  const ext = extOf(videoPath);
  if (ext === null) return null;
  const base = videoPath.slice(0, videoPath.length - ext.length - 1);
  return { first: `${base}_first.jpg`, last: `${base}_last.jpg` };
}

/** 首 / 尾帧路径 → 帧类型与可能对应的视频路径（扩展名未知，列出全部候选，供精确匹配 / 走索引）。 */
export function videoPathsForFrame(framePath: string): { frame: 'first' | 'last'; videos: string[] } | null {
  const m = framePath.match(/^(.*)_(first|last)\.jpg$/i);
  if (!m) return null;
  return {
    frame: m[2].toLowerCase() as 'first' | 'last',
    videos: VIDEO_EXTS.flatMap((ext) => [`${m[1]}.${ext}`, `${m[1]}.${ext.toUpperCase()}`]),
  };
}

/** 首帧路径 → 可能对应的视频路径。 */
export function videoPathsForPoster(posterPath: string): string[] | null {
  const mapped = videoPathsForFrame(posterPath);
  return mapped?.frame === 'first' ? mapped.videos : null;
}

/**
 * 由视频地址推导首 / 尾帧地址（rendi 写在同目录：<videoBase>_first.jpg / _last.jpg，去掉查询参数）。
 * 相对地址按当前页面解析；无法推导时返回 undefined。前端与抽帧接口共用这一条规则。
 */
export function videoPosterUrl(videoUrl: string, mode: 'first' | 'last' = 'first'): string | undefined {
  try {
    const url = new URL(String(videoUrl), typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const file = url.pathname.slice(url.pathname.lastIndexOf('/') + 1);
    const dot = file.lastIndexOf('.');
    if (dot < 0) return undefined;
    url.pathname = url.pathname.replace(/[^/]+$/, `${file.slice(0, dot)}_${mode}.jpg`);
    url.search = '';
    return url.toString();
  } catch {
    return undefined;
  }
}

/**
 * 存储路径是否位于该用户自己的目录（<userId>/...）。所有上传都写在上传者目录下；
 * file_asset 的 storage_path 可由客户端登记，只有满足这条才可信 —— 据此才会删除存储文件或派发缩略图任务，
 * 防止把他人的文件登记到自己名下后删除或处理。
 */
export function isOwnedStoragePath(userId: string, storagePath: string): boolean {
  return Boolean(userId) && storagePath.startsWith(`${userId}/`) && !storagePath.includes('..');
}

/** 自定义存储域名（与 next.config.ts images.remotePatterns 保持一致）。 */
export const CUSTOM_STORAGE_HOSTS = ['db.flowmuse.ai'] as const;

/**
 * 判断 Supabase Storage 的错误响应是否表示「对象不存在」。
 * Storage 对不存在的对象常返回 HTTP 400 + {"statusCode":"404","error":"not_found"}，
 * 但鉴权失败、锁超时、后端 503 等也返回 400，不能一概视为不存在。
 */
export function isStorageNotFound(status: number, bodyText: string): boolean {
  if (status === 404) return true;
  if (status !== 400) return false;
  try {
    const body = JSON.parse(bodyText) as { statusCode?: unknown; error?: unknown; message?: unknown };
    return (
      String(body.statusCode) === '404' ||
      /not[_ ]?found/i.test(String(body.error ?? '')) ||
      /not found/i.test(String(body.message ?? ''))
    );
  } catch {
    return false;
  }
}

/** 由缩略图路径（或地址）还原其源文件路径（或地址）；不是缩略图时返回 null。 */
export function thumbnailSourcePath(thumbPath: string): string | null {
  const source = thumbPath.replace(/_thumb(?:_sm|_xl)?\.webp$/i, '');
  return source === thumbPath ? null : source;
}

// ── Inngest 事件（主站 / admin 发送，media 服务消费）──

export type MediaThumbnailRequestedData = {
  bucket: string;
  storagePath: string;
  /** 对应 file_asset.id（已知时携带，用于回写状态；视频行对应其首帧 _first.jpg） */
  assetId?: string;
  /** 无条件重新生成，并绕过 CDN 读取原图（admin 手动重新生成） */
  force?: boolean;
  /**
   * 源文件以覆盖方式写入（upsert）：已有缩略图时比较修改时间，缩略图比原图旧才重新生成（并绕过 CDN）。
   * 文件名唯一、从未被覆盖的上传不会因此重复生成。
   */
  ifStale?: boolean;
};

export type MediaBackfillRequestedData = {
  /** media_job.id */
  jobId: string;
};

export type MediaEvents = {
  /** 上传、浏览时补生成、admin 手动操作：优先处理 */
  'media/thumbnail.requested': { data: MediaThumbnailRequestedData };
  /** 补生成任务派发：与上面同一处理函数，但优先级低，不挤占实时任务 */
  'media/thumbnail.backfill': { data: MediaThumbnailRequestedData };
  'media/backfill.requested': { data: MediaBackfillRequestedData };
};
